import { useId, useState } from 'react';

/* Graphique de comparaison N / N-1.
 *
 * ── Couleurs : mesurées, pas choisies à l'œil ───────────────────────────────
 *
 * Les catégories (clubs, types de faute) sont nominales : leur identité est
 * portée par le libellé, pas par la teinte. Une palette catégorielle
 * dépenserait le canal couleur à ré-encoder ce que la longueur de barre montre
 * déjà. D'où UNE seule teinte pour la séance en cours, et un gris neutre pour
 * la référence.
 *
 * Mesuré par thème, contre la carte qui porte le graphique et sous un voile
 * solaire de 8 % :
 *
 *   sombre  actuelle 6,33:1   précédente 5,59:1   grille 1,63:1
 *   clair   actuelle 4,24:1   précédente 3,50:1   grille 1,33:1
 *
 * Le lime de marque est hors bande pour un aplat — trop clair, il hurle sur de
 * grandes surfaces. Il reste sur les boutons et les chiffres clés ; les barres
 * prennent le cran plus profond de la même famille.
 *
 * La séparation des DEUX SÉRIES entre elles reste faible (1,13:1 en sombre,
 * 1,21:1 en clair) : légale uniquement avec un encodage secondaire. Il y en a
 * trois ici — la référence est un CONTOUR quand l'actuelle est un aplat (deux
 * formes différentes), chaque barre porte sa valeur en clair, et le tableau
 * équivalent se déplie sous le graphique.
 *
 * ── Géométrie ───────────────────────────────────────────────────────────────
 *
 * Le libellé est AU-DESSUS de sa barre, pas à côté. Un « Fer 7 · TaylorMade
 * 33.5° » fait environ 145 px à 13 px : dans une gouttière latérale, il serait
 * rogné. Au-dessus, il dispose de toute la largeur, et la barre occupe la
 * pleine échelle — ce qui rend aussi les longueurs plus faciles à comparer
 * d'une ligne à l'autre.
 */

/* Les couleurs viennent du THÈME, plus d'un hexadécimal écrit ici.
 *
 * Les anciennes valeurs étaient accordées au seul fond sombre #18281e. Sur le
 * thème clair elles devenaient un vert pâle sur blanc — 1,4:1, illisible. Les
 * jetons `--chart-*` sont déclarés par thème dans index.css et validés contre
 * la surface de chaque thème, voile solaire compris. */
const C_CURRENT = 'var(--chart-current)';
const C_PREVIOUS = 'var(--chart-previous)';
const C_GRID = 'var(--chart-grid)';

export interface ComparisonRow {
    key: string;
    label: string;
    current: number;
    previous: number | null;
}

interface Props {
    rows: ComparisonRow[];
    /** Valeur haute de l'axe. 100 pour un pourcentage, sinon le max arrondi. */
    max: number;
    unit?: string;
    currentLabel: string;
    previousLabel: string;
    digits?: number;
}

const W = 560;
const PAD_R = 4;
const ROW_H = 60;
const LABEL_Y = 12;   // ligne de texte
// La silhouette monte 5 px au-dessus de la barre (GHOST_H > BAR_H). Sans marge
// suffisante, son bord supérieur frôle la ligne de texte et se lit comme un
// soulignement du libellé — vu à la capture, corrigé en descendant les barres.
const BAR_TOP = 34;
const BAR_H = 12;     // marque fine
const GHOST_H = 22;   // la silhouette encadre le plein

export function ComparisonBars({
    rows, max, unit = '', currentLabel, previousLabel, digits = 0,
}: Props) {
    const titleId = useId();
    const [table, setTable] = useState(false);
    const [hover, setHover] = useState<string | null>(null);

    const plotW = W - PAD_R;
    const height = rows.length * ROW_H;
    const scale = (v: number): number => (Math.max(0, Math.min(v, max)) / max) * plotW;
    // En FRANÇAIS : virgule décimale. `toFixed` écrit « 41.2 », qui voisinait
    // « 58,8 » sur le même écran.
    const nb = (v: number): string =>
        v.toLocaleString('fr-FR', { minimumFractionDigits: digits, maximumFractionDigits: digits });
    const fmt = (v: number): string => `${nb(v)}${unit}`;

    return (
        <div>
            {/* Deux séries : la légende est toujours présente, et l'identité ne
                repose jamais sur la couleur seule. */}
            <div className="mb-2 flex flex-wrap items-center gap-4 text-xs">
                <span className="flex items-center gap-1.5">
                    <span className="inline-block h-3 w-4 rounded-sm" style={{ background: C_CURRENT }} />
                    {currentLabel}
                </span>
                <span className="flex items-center gap-1.5">
                    <span className="inline-block h-3 w-4 rounded-sm border-2 bg-transparent"
                        style={{ borderColor: C_PREVIOUS }} />
                    {previousLabel}
                </span>
                <button type="button" className="btn btn-ghost btn-xs ml-auto"
                    onClick={() => setTable((t) => !t)} aria-expanded={table}>
                    {table ? 'Masquer' : 'Voir'} le tableau
                </button>
            </div>

            <svg
                viewBox={`0 0 ${W} ${height}`}
                className="h-auto w-full"
                role="img"
                aria-labelledby={titleId}
            >
                <title id={titleId}>
                    {currentLabel} comparé à {previousLabel}, par catégorie
                </title>

                {/* Grille : filets pleins, un ton au-dessus du fond. Jamais de
                    pointillés, qui se liraient comme un seuil. */}
                {[0.25, 0.5, 0.75, 1].map((t) => (
                    <line key={t}
                        x1={t * plotW} x2={t * plotW} y1={0} y2={height}
                        stroke={C_GRID} strokeWidth={1} />
                ))}

                {rows.map((r, i) => {
                    const y = i * ROW_H;
                    const on = hover === r.key;
                    const delta = r.previous != null ? r.current - r.previous : null;
                    return (
                        <g key={r.key}
                            onMouseEnter={() => setHover(r.key)}
                            onMouseLeave={() => setHover(null)}
                        >
                            {/* Cible de survol : toute la ligne, largement
                                au-dessus des 24 px minimum. */}
                            <rect x={0} y={y} width={W} height={ROW_H}
                                fill={on ? '#ffffff0a' : 'transparent'} />

                            <text x={0} y={y + LABEL_Y} dominantBaseline="central"
                                className="fill-base-content text-[13px]">
                                {r.label}
                            </text>

                            {/* Étiquette directe, en encre de texte et non dans
                                la couleur de la série. L'écart n'apparaît qu'au
                                survol : un nombre sur chaque point serait du
                                bruit. */}
                            <text x={W} y={y + LABEL_Y} textAnchor="end" dominantBaseline="central"
                                className="fill-base-content text-[13px] font-semibold tabular-nums">
                                {fmt(r.current)}
                                {on && delta != null && (
                                    <tspan className="fill-base-content/50 font-normal">
                                        {'  '}{delta >= 0 ? '+' : ''}{nb(delta)}{unit} vs {fmt(r.previous!)}
                                    </tspan>
                                )}
                            </text>

                            {/* Séance précédente : contour, en arrière-plan.
                                Une forme différente de l'aplat, donc lisible
                                sans distinguer les deux teintes. */}
                            {r.previous != null && r.previous > 0 && (
                                <rect
                                    x={0} y={y + BAR_TOP + (BAR_H - GHOST_H) / 2}
                                    width={Math.max(scale(r.previous), 2)} height={GHOST_H}
                                    rx={4} fill="none" stroke={C_PREVIOUS} strokeWidth={1.5}
                                />
                            )}

                            {/* Séance actuelle : aplat fin, ancré à l'origine,
                                extrémité arrondie à 4 px.
                                Rien n'est dessiné à zéro — un moignon de 2 px
                                se lirait comme « un peu » là où il n'y a rien. */}
                            {r.current > 0 && (
                                <rect
                                    x={0} y={y + BAR_TOP}
                                    width={Math.max(scale(r.current), 2)} height={BAR_H}
                                    rx={4} fill={C_CURRENT}
                                />
                            )}
                        </g>
                    );
                })}

                <line x1={0} x2={0} y1={0} y2={height} stroke={C_GRID} strokeWidth={1} />
            </svg>

            {/* Jumeau tabulaire : toute valeur du graphique est lisible ici, au
                clavier comme au lecteur d'écran. */}
            {table && (
                <div className="mt-3 overflow-x-auto">
                    <table className="table table-sm">
                        <thead>
                            <tr>
                                <th />
                                <th className="text-right">{currentLabel}</th>
                                <th className="text-right">{previousLabel}</th>
                                <th className="text-right">Écart</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.key}>
                                    <td>{r.label}</td>
                                    <td className="text-right tabular-nums">{fmt(r.current)}</td>
                                    <td className="text-right tabular-nums">
                                        {r.previous != null ? fmt(r.previous) : '—'}
                                    </td>
                                    <td className="text-right tabular-nums">
                                        {r.previous != null
                                            ? `${r.current >= r.previous ? '+' : ''}${nb(r.current - r.previous)}${unit}`
                                            : '—'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
